import { useState, useCallback, type KeyboardEvent } from 'react';
import { motion } from 'motion/react';
import { Star } from 'lucide-react';
import { cn } from './cn';

export interface RatingProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'onChange' | 'role' | 'aria-valuenow' | 'children'
  > {
  /** Current rating value (supports fractions in read-only mode). */
  value: number;
  /** Called with the new value when an interactive star is selected. */
  onChange?: (value: number) => void;
  /** Number of stars. Default 5. */
  max?: number;
  /** Render as a non-interactive display, supporting fractional fill. */
  readOnly?: boolean;
  /** Star size in px. Default 20. */
  size?: number;
  className?: string;
  'aria-label'?: string;
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/**
 * Star rating — replaces MUI <Rating />.
 *
 * - Read-only: fractional fill of the last partial star via a clipped overlay.
 * - Interactive (onChange present): hover preview, click to set, keyboard
 *   ArrowLeft/Right to adjust. Exposed as an accessible slider.
 */
export function Rating({
  value,
  onChange,
  max = 5,
  readOnly = false,
  size = 20,
  className,
  'aria-label': ariaLabel,
  ...props
}: RatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const interactive = Boolean(onChange) && !readOnly;

  const safeValue = clamp(value || 0, 0, max);
  // The value that drives the visual fill (hover preview wins when interactive).
  const display = interactive && hovered !== null ? hovered : safeValue;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!interactive) return;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          next = clamp(safeValue + 1, 0, max);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          next = clamp(safeValue - 1, 0, max);
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      if (next !== null && next !== safeValue) onChange?.(next);
    },
    [interactive, safeValue, max, onChange],
  );

  return (
    <div
      role={interactive ? 'slider' : 'img'}
      aria-label={ariaLabel}
      aria-readonly={readOnly || undefined}
      aria-valuemin={interactive ? 0 : undefined}
      aria-valuemax={interactive ? max : undefined}
      aria-valuenow={interactive ? safeValue : undefined}
      aria-valuetext={!interactive ? `${safeValue} / ${max}` : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onMouseLeave={interactive ? () => setHovered(null) : undefined}
      className={cn(
        'inline-flex items-center gap-0.5',
        interactive &&
          'cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
      {...props}
    >
      {Array.from({ length: max }, (_, i) => {
        const starValue = i + 1;
        // Fraction filled for this star: 1 fully, 0 empty, between for partial.
        const fill = clamp(display - i, 0, 1);
        const isActive = fill > 0;

        const StarContent = (
          <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
            {/* Empty base */}
            <Star
              size={size}
              strokeWidth={2}
              className="text-line transition-colors duration-200"
              aria-hidden="true"
            />
            {/* Filled overlay, clipped to the fractional width */}
            {isActive && (
              <span
                className="pointer-events-none absolute inset-0 overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <Star
                  size={size}
                  strokeWidth={2}
                  className="fill-primary text-primary transition-colors duration-200"
                  aria-hidden="true"
                />
              </span>
            )}
          </span>
        );

        if (!interactive) {
          return <span key={starValue}>{StarContent}</span>;
        }

        return (
          <motion.button
            key={starValue}
            type="button"
            tabIndex={-1}
            aria-label={`${starValue}`}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            onMouseEnter={() => setHovered(starValue)}
            onFocus={() => setHovered(starValue)}
            onBlur={() => setHovered(null)}
            onClick={() => onChange?.(starValue)}
            className="inline-flex outline-none"
          >
            {StarContent}
          </motion.button>
        );
      })}
    </div>
  );
}

Rating.displayName = 'Rating';
